import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  Avatar,
  Chip,
  Fade,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Edit as EditIcon,
  ViewColumn as ColumnIcon,
  Description as DescriptionIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Functions as FunctionsIcon,
} from '@mui/icons-material';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import FormulaEditor from './FormulaEditor';
import { TableService } from '../services/table.service';
import { DataTable, TableColumn, ColumnType, ColumnFormula } from '../types/table.types';

interface TableFormData {
  name: string;
  description?: string;
}

const tableSchema = yup.object({
  name: yup.string().required('Название таблицы обязательно'),
  description: yup.string().optional(),
}).required();

interface ColumnFormData {
  id?: string;
  name: string;
  type: ColumnType;
  description?: string;
  isRequired?: boolean;
  isUnique?: boolean;
  options?: any;
  formula?: ColumnFormula | null;
}

const columnSchema = yup.object({
  name: yup.string().required('Название колонки обязательно'),
  type: yup.mixed<ColumnType>().required('Тип колонки обязателен'),
  description: yup.string().optional(),
  isRequired: yup.boolean().optional(),
  isUnique: yup.boolean().optional(),
}).required();

export default function TableEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [table, setTable] = useState<DataTable | null>(null);
  const [columns, setColumns] = useState<ColumnFormData[]>([]);
  const [showColumnForm, setShowColumnForm] = useState(false);
  const [editingColumnIndex, setEditingColumnIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<{ index: number, name: string } | null>(null);
  const [mounted, setMounted] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [columnFormula, setColumnFormula] = useState<ColumnFormula | null>(null);

  const { control: tableControl, handleSubmit: handleTableSubmit, formState: { errors: tableErrors }, setValue: setTableValue } = useForm<TableFormData>({
    resolver: yupResolver(tableSchema) as Resolver<TableFormData>,
    defaultValues: { name: '', description: '' }
  });

  const { control: columnControl, handleSubmit: handleColumnSubmit, formState: { errors: columnErrors }, reset: resetColumnForm, setValue: setColumnValue, watch: watchColumn } = useForm<ColumnFormData>({
    resolver: yupResolver(columnSchema) as Resolver<ColumnFormData>,
    defaultValues: { name: '', type: ColumnType.TEXT, description: '', isRequired: false, isUnique: false }
  });

  const watchColumnType = watchColumn('type');
  const watchColumnName = watchColumn('name');

  useEffect(() => {
    setMounted(true);
    if (id) {
      fetchTable(id);
    }
  }, [id]);

  const fetchTable = async (tableId: string) => {
    try {
      setLoading(true);
      const data = await TableService.getTableById(tableId);
      setTable(data);

      setTableValue('name', data.name);
      setTableValue('description', data.description || '');

      const sortedColumns = [...data.columns].sort((a, b) => a.displayOrder - b.displayOrder);
      setColumns(sortedColumns.map(column => ({
        id: column.id,
        name: column.name,
        type: column.type,
        description: column.description || '',
        isRequired: column.isRequired,
        isUnique: column.isUnique,
        options: column.options,
        formula: column.formula || null,
      })));

      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки таблицы');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = () => {
    const trimmed = newOption.trim();
    if (trimmed && !dropdownOptions.includes(trimmed)) {
      setDropdownOptions([...dropdownOptions, trimmed]);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setDropdownOptions(dropdownOptions.filter((_, i) => i !== index));
  };

  const handleCloseColumnForm = () => {
    setShowColumnForm(false);
    setEditingColumnIndex(null);
    setDropdownOptions([]);
    setNewOption('');
    setColumnFormula(null);
  };

  const handleSaveColumn = async (data: ColumnFormData) => {
    if (!id) return;

    const columnData = { ...data };
    if (data.type === ColumnType.DROPDOWN) {
      columnData.options = { values: dropdownOptions };
    }
    columnData.formula = columnFormula;

    try {
      if (editingColumnIndex !== null) {
        const column = columns[editingColumnIndex];

        if (column.id) {
          const { id: columnId, ...rest } = { ...column, ...columnData };
          await TableService.updateColumn(id, column.id, {
            ...rest,
            displayOrder: editingColumnIndex,
          });
        }

        const newColumns = [...columns];
        newColumns[editingColumnIndex] = { ...column, ...columnData };
        setColumns(newColumns);

      } else {
        const { id: _id, ...columnWithoutId } = columnData;
        const savedColumn = await TableService.addColumn(id, {
          ...columnWithoutId,
          displayOrder: columns.length,
        });

        setColumns([...columns, { ...columnData, id: savedColumn.id }]);
        setSuccess('Колонка добавлена');
      }

      resetColumnForm();
      setShowColumnForm(false);
      setEditingColumnIndex(null);
      setDropdownOptions([]);
      setNewOption('');
      setColumnFormula(null);
      setError(null);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка сохранения колонки');
    }
  };

  const handleEditColumn = (index: number) => {
    const column = columns[index];

    setColumnValue('name', column.name);
    setColumnValue('type', column.type);
    setColumnValue('description', column.description || '');
    setColumnValue('isRequired', column.isRequired);
    setColumnValue('isUnique', column.isUnique);

    setDropdownOptions(column.options?.values || []);
    setNewOption('');
    setColumnFormula(column.formula || null);

    setEditingColumnIndex(index);
    setShowColumnForm(true);
  };

  const handleMoveColumnUp = (index: number) => {
    if (index <= 0) return;
    const newColumns = [...columns];
    const temp = newColumns[index - 1];
    newColumns[index - 1] = newColumns[index];
    newColumns[index] = temp;
    setColumns(newColumns);
  };

  const handleMoveColumnDown = (index: number) => {
    if (index >= columns.length - 1) return;
    const newColumns = [...columns];
    const temp = newColumns[index + 1];
    newColumns[index + 1] = newColumns[index];
    newColumns[index] = temp;
    setColumns(newColumns);
  };

  const handleConfirmDelete = (index: number) => {
    setColumnToDelete({ index, name: columns[index].name });
    setConfirmDeleteOpen(true);
  };

  const handleRemoveColumn = async () => {
    if (!columnToDelete) return;

    const { index } = columnToDelete;
    const column = columns[index];

    setConfirmDeleteOpen(false);

    if (column.id && id) {
      try {
        await TableService.deleteColumn(id, column.id);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Ошибка удаления колонки');
        return;
      }
    }

    setColumns(columns.filter((_, i) => i !== index));
    setColumnToDelete(null);
  };

  const handleCancel = () => {
    navigate(`/tables/${id}`);
  };

  const onSubmitTable = async (data: TableFormData) => {
    if (!id) return;

    if (columns.length === 0) {
      setError('Необходимо добавить хотя бы одну колонку');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await TableService.updateTable(id, {
        name: data.name,
        description: data.description,
      });

      for (let i = 0; i < columns.length; i++) {
        const column = columns[i];

        if (column.id) {
          await TableService.updateColumn(id, column.id, {
            ...column,
            displayOrder: i,
          });
        } else {
          await TableService.addColumn(id, {
            ...column,
            displayOrder: i,
          });
        }
      }

      setSuccess('Таблица успешно обновлена');
      setTimeout(() => {
        navigate(`/tables/${id}`);
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка обновления таблицы');
    } finally {
      setSubmitting(false);
    }
  };

  // Все колонки для выбора source в формуле
  const availableColumnsForFormula = columns.map(c => ({ name: c.name, type: c.type }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (!table) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Таблица не найдена или у вас нет доступа к ней
        </Typography>
        <Button variant="contained" onClick={() => navigate('/tables')} sx={{ mt: 2 }}>
          Вернуться к списку таблиц
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header with Gradient */}
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <EditIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Редактирование таблицы
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                  {table.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      {error && (
        <Fade in timeout={400}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        </Fade>
      )}

      {success && (
        <Fade in timeout={400}>
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {success}
          </Alert>
        </Fade>
      )}

      {/* Main Info Card */}
      <Fade in={mounted} timeout={600}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              p: 2.5,
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DescriptionIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Основная информация
              </Typography>
            </Box>
          </Box>

          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="name"
                  control={tableControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Название таблицы"
                      fullWidth
                      required
                      error={!!tableErrors.name}
                      helperText={tableErrors.name?.message}
                      disabled={submitting}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#667eea' },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' },
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="description"
                  control={tableControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Описание"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!tableErrors.description}
                      helperText={tableErrors.description?.message}
                      disabled={submitting}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#667eea' },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' },
                        }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      {/* Columns Card */}
      <Fade in={mounted} timeout={800}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              p: 2.5,
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ColumnIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Колонки
                </Typography>
                <Chip
                  label={columns.length}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetColumnForm();
                  setEditingColumnIndex(null);
                  setDropdownOptions([]);
                  setNewOption('');
                  setColumnFormula(null);
                  setShowColumnForm(true);
                }}
                disabled={submitting || showColumnForm}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Добавить колонку
              </Button>
            </Box>
          </Box>

          <CardContent sx={{ p: 3 }}>
            <Collapse in={showColumnForm}>
              <Box sx={{
                mb: 3,
                p: 3,
                bgcolor: 'rgba(240, 147, 251, 0.08)',
                borderRadius: 2,
                border: '2px dashed rgba(240, 147, 251, 0.3)'
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#f5576c' }}>
                  {editingColumnIndex !== null ? 'Редактирование колонки' : 'Добавление колонки'}
                </Typography>

                <form onSubmit={handleColumnSubmit(handleSaveColumn)}>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="name"
                        control={columnControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Название колонки"
                            fullWidth
                            required
                            error={!!columnErrors.name}
                            helperText={columnErrors.name?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="type"
                        control={columnControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Тип данных"
                            fullWidth
                            required
                            error={!!columnErrors.type}
                            helperText={columnErrors.type?.message}
                            disabled={editingColumnIndex !== null}
                            onChange={(e) => {
                              field.onChange(e);
                              if (e.target.value !== ColumnType.DROPDOWN) {
                                setDropdownOptions([]);
                                setNewOption('');
                              }
                            }}
                          >
                            <MenuItem value={ColumnType.TEXT}>Текст</MenuItem>
                            <MenuItem value={ColumnType.NUMBER}>Число</MenuItem>
                            <MenuItem value={ColumnType.DATE}>Дата</MenuItem>
                            <MenuItem value={ColumnType.BOOLEAN}>Да/Нет</MenuItem>
                            <MenuItem value={ColumnType.DROPDOWN}>Выпадающий список</MenuItem>
                          </TextField>
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="description"
                        control={columnControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Описание"
                            fullWidth
                            error={!!columnErrors.description}
                            helperText={columnErrors.description?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="isRequired"
                        control={columnControl}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Checkbox checked={field.value} onChange={field.onChange} />}
                            label="Обязательное поле"
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="isUnique"
                        control={columnControl}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Checkbox checked={field.value} onChange={field.onChange} />}
                            label="Уникальное значение"
                          />
                        )}
                      />
                    </Grid>

                    {/* Dropdown options */}
                    {watchColumnType === ColumnType.DROPDOWN && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Варианты выбора
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                          <TextField
                            size="small"
                            fullWidth
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            placeholder="Введите вариант и нажмите Enter"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddOption();
                              }
                            }}
                          />
                          <Button
                            variant="outlined"
                            onClick={handleAddOption}
                            sx={{ minWidth: 44 }}
                          >
                            <AddIcon />
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {dropdownOptions.map((opt, i) => (
                            <Chip
                              key={i}
                              label={opt}
                              onDelete={() => handleRemoveOption(i)}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                        {dropdownOptions.length === 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Добавьте хотя бы один вариант для выпадающего списка
                          </Typography>
                        )}
                      </Grid>
                    )}

                    {/* ✅ Formula Editor */}
                    <Grid size={{ xs: 12 }}>
                      <FormulaEditor
                        value={columnFormula}
                        onChange={setColumnFormula}
                        availableColumns={availableColumnsForFormula}
                        currentColumnName={watchColumnName}
                        disabled={submitting}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={handleCloseColumnForm}
                          startIcon={<CancelIcon />}
                        >
                          Отмена
                        </Button>

                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={watchColumnType === ColumnType.DROPDOWN && dropdownOptions.length === 0}
                          sx={{
                            bgcolor: '#f5576c',
                            '&:hover': { bgcolor: '#e04858' }
                          }}
                        >
                          {editingColumnIndex !== null ? 'Сохранить' : 'Добавить'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </Collapse>

            {columns.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <ColumnIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Нет добавленных колонок
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {columns.map((column, index) => (
                  <ListItem
                    key={index}
                    divider={index < columns.length - 1}
                    sx={{
                      py: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(240, 147, 251, 0.05)',
                      }
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'rgba(245, 87, 108, 0.1)', color: '#f5576c', mr: 2 }}>
                      <SettingsIcon />
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {column.name}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={getColumnTypeName(column.type)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {column.isRequired && (
                              <Chip label="Обязательное" size="small" color="error" variant="outlined" />
                            )}
                            {column.isUnique && (
                              <Chip label="Уникальное" size="small" color="success" variant="outlined" />
                            )}
                            {column.formula && (
                              <Chip
                                icon={<FunctionsIcon sx={{ fontSize: '0.85rem !important' }} />}
                                label={column.formula.type}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                                  color: '#667eea',
                                  fontWeight: 600,
                                  '& .MuiChip-icon': { color: '#667eea' },
                                }}
                              />
                            )}
                          </Box>
                          {column.type === ColumnType.DROPDOWN && column.options?.values?.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {column.options.values.map((val: string, i: number) => (
                                <Chip key={i} label={val} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditColumn(index)}
                        disabled={submitting}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        edge="end"
                        onClick={() => handleMoveColumnUp(index)}
                        disabled={index === 0 || submitting}
                      >
                        <ArrowUpIcon />
                      </IconButton>

                      <IconButton
                        edge="end"
                        onClick={() => handleMoveColumnDown(index)}
                        disabled={index === columns.length - 1 || submitting}
                      >
                        <ArrowDownIcon />
                      </IconButton>

                      <IconButton
                        edge="end"
                        onClick={() => handleConfirmDelete(index)}
                        disabled={submitting}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Action Buttons */}
      <Fade in={mounted} timeout={1000}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={submitting}
            startIcon={<CancelIcon />}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#667eea',
                bgcolor: 'rgba(102, 126, 234, 0.08)',
              }
            }}
          >
            Отмена
          </Button>

          <Button
            variant="contained"
            onClick={handleTableSubmit(onSubmitTable)}
            disabled={submitting || columns.length === 0}
            startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            {submitting ? 'Сохранение...' : 'Сохранить таблицу'}
          </Button>
        </Box>
      </Fade>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Удаление колонки"
        message={`Вы уверены, что хотите удалить колонку "${columnToDelete?.name}"? Все данные, связанные с этой колонкой, будут потеряны.`}
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={handleRemoveColumn}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setColumnToDelete(null);
        }}
        severity="error"
      />
    </Box>
  );
}

function getColumnTypeName(type: ColumnType): string {
  switch (type) {
    case ColumnType.TEXT:
      return 'Текст';
    case ColumnType.NUMBER:
      return 'Число';
    case ColumnType.DATE:
      return 'Дата';
    case ColumnType.BOOLEAN:
      return 'Да/Нет';
    case ColumnType.DROPDOWN:
      return 'Выпадающий список';
    case ColumnType.REFERENCE:
      return 'Ссылка';
    default:
      return 'Неизвестный тип';
  }
}